<template>
  <v-dialog
    v-model="isVisible"
    :fullscreen="$vuetify.breakpoint.xs"
    height="64"
    max-height="64"
    max-width="550"
    persistent
    scrollable
  >
    <v-card v-if="editItem">
      <v-toolbar
        flat
      >
        <v-avatar
          class="mr-3"
          color="blue"
        >
          <v-icon color="blue lighten-5">
            {{ editItem.Id ? $t('Marketing.Sms.Dialog.EditTemplate.Toolbar.Icon') : $t('Marketing.Sms.Dialog.AddTemplate.Toolbar.Icon') }}
          </v-icon>
        </v-avatar>


        <v-toolbar-title
          class="pl-1"
        >
          <div class="body-3">
            {{ editItem.Id ? $t('Marketing.Sms.Dialog.EditTemplate.Toolbar.Title') : $t('Marketing.Sms.Dialog.AddTemplate.Toolbar.Title') }}
          </div>
          <div class="text-caption">
            {{ editItem.Id ? $t('Marketing.Sms.Dialog.EditTemplate.Toolbar.SubTitle') : $t('Marketing.Sms.Dialog.AddTemplate.Toolbar.SubTitle') }}
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          class="ml-0"
          icon
          @click="onCancelButtonClick"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text class="pa-2 fill-height">
        <v-form
          ref="dataForm"
          v-model="dataFormValid"
          @submit.prevent
        >
          <v-container
            class="pa-0"
            fluid
          >
            <v-row dense>
              <v-col>
                <v-text-field
                  v-model="editItem.Title"
                  :hint="$t('Marketing.Sms.Dialog.EditTemplate.Field.Title.Hint')"
                  :label="$t('Marketing.Sms.Dialog.EditTemplate.Field.Title.Label')"
                  :prepend-icon="$t('Marketing.Sms.Dialog.EditTemplate.Field.Title.Icon')"
                  :rules="formFieldRules.Title"
                  clearable
                  filled
                  hide-details="auto"
                />
              </v-col>

              <v-col
                class="text-end"
                cols="auto"
              >
                <div class="d-inline-flex mx-1">
                  <div>
                    <v-subheader
                      class="pa-0 py-1 justify-end"
                      style="height: auto; min-width: 85px;"
                    >
                      Use Unicode
                    </v-subheader>

                    <v-switch
                      v-model="editItem.UseUnicodeCharset"
                      :label="editItem.UseUnicodeCharset ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="pa-0 ma-0"
                      color="orange"
                      hide-details
                      inset
                      @change="onlyGsmCharactersMask(editItem.Body)"
                    />
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12">
                <v-textarea
                  v-model="editItem.Body"
                  :hint="$t('Marketing.Sms.Dialog.EditTemplate.Field.Body.Hint')"
                  :label="$t('Marketing.Sms.Dialog.EditTemplate.Field.Body.Label')"
                  :prepend-icon="$t('Marketing.Sms.Dialog.EditTemplate.Field.Body.Icon')"
                  :rules="formFieldRules.Body"
                  clearable
                  counter
                  filled
                  hide-details="auto"
                  rows="5"
                  @input="onlyGsmCharactersMask(editItem.Body)"
                >
                  <template #counter>
                    <div class="v-counter ma-0 fill-width text-right">
                      <div>
                        <span class="orange--text">{{ countSmsTextProp.messages }} {{ $t('Marketing.Sms.Dialog.EditCampaign.Field.Info.Sms') }}</span> /
                        <span class="info--text">{{ countSmsTextProp.length }} {{ $tc('Marketing.Sms.Dialog.EditCampaign.Field.Info.Characters', countSmsTextProp.length) }}</span>
                      </div>

                      <div
                        class="mt-3 info--text"
                        style="font-size: 11px"
                      >
                        <div>{{ countSmsTextProp.encoding.replaceAll('_', '-') }} {{ $t('Marketing.Sms.Dialog.EditCampaign.Field.Info.Encoding') }}</div>
                        <div>
                          {{ countSmsTextProp.characterPerMessage }} {{ $tc('Marketing.Sms.Dialog.EditCampaign.Field.Info.Characters', countSmsTextProp.characterPerMessage) }} /
                          {{ $t('Marketing.Sms.Dialog.EditCampaign.Field.Info.Sms') }}
                        </div>
                      </div>
                    </div>
                  </template>
                </v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-2">
        <v-spacer />

        <v-btn
          class="light-blue--text text--darken-1"
          outlined
          @click="onCancelButtonClick"
        >
          {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
        </v-btn>

        <v-btn
          :disabled="formIsSaving"
          :loading="formIsSaving"
          color="green"
          dark
          depressed
          @click="onSaveButtonClick"
        >
          {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Auth from '@/mixins/auth'
import DialogVisible from '@/mixins/dialogVisible'
import {clone, mergeDeep} from '@/lib/helper/helper.js'
import {convertToGsm, smsTextInfo} from '@/lib/sms/smsTextInfo.js'

export default {
  name      : 'SmsTemplateEditDialog',
  components: {},
  mixins    : [Auth, DialogVisible],
  props     : {
    template: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      isFullScreen  : true,
      dataFormValid : false,
      editItem      : null,
      defaultDataObj: {
        Id               : null,
        Title            : '',
        Body             : '',
        Type             : 'sms',
        UseUnicodeCharset: false
      },
      formIsSaving  : false,
      formFieldRules: {
        Title: [
          (v) => !!v || this.$t('Marketing.Sms.Dialog.EditTemplate.Field.Title.Error.Required'),
          (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Marketing.Sms.Dialog.EditTemplate.Field.Title.Error.Between')
        ],
        Body: [
          (v) => !!v || this.$t('Marketing.Sms.Dialog.EditTemplate.Field.Body.Error.Required')
        ]
      }
    }
  },
  computed: {
    countSmsTextProp() {
      return this.countSmsText(this.editItem?.Body || '')
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.initDialog()
      } else {
        this.destroyDialog()
      }
    }
  },
  created() {
    this.initDialog()
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.destroyDialog()
  },
  methods: {
    initDialog() {
      this.editItem = clone(mergeDeep(this.defaultDataObj, this.template || {}))
      this.onlyGsmCharactersMask(this.editItem.Body)
      this.initEvents()
    },
    destroyDialog() {
      this.editItem = null
      this.destroyEvents()
    },
    initEvents() {
      this.destroyEvents()
      this.$bus.$on(window.SocketCommand.Marketing.Sms.Template.Save, this.onSaveResult)
    },
    destroyEvents() {
      this.$bus.$off(window.SocketCommand.Marketing.Sms.Template.Save, this.onSaveResult)
    },

    onSaveButtonClick() {
      if (!this.$refs.dataForm.validate()) return

      this.formIsSaving = true
      const payload = clone(this.editItem)
      window.callAS(window.SocketCommand.Marketing.Sms.Template.Save, payload)
    },

    onSaveResult(data) {
      this.formIsSaving = false

      if (data && data.status === 'success') {
        if (data?.Id) this.editItem.Id = data.Id
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Marketing.Sms.Dialog.EditTemplate.Message.Save.Success'),
          type: 'success',
          icon: 'info'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    countSmsText(text) {
      return smsTextInfo(text || '')
    },

    convertSmsTextToGsm(text) {
      return convertToGsm(text || '')
    },

    onlyGsmCharactersMask(text) {
      if (this.editItem?.UseUnicodeCharset) return

      const sms = this.convertSmsTextToGsm(text || '')
      this.editItem.Body = sms //.toUpperCase()
    },

    onCancelButtonClick() {
      this.closeDialog()
    }
  }
}
</script>
